import React, { useEffect, useRef } from 'react';
import { colors as theme } from '../../theme';

const ROW_HEIGHT = 40;
const VISIBLE_ROWS = 5;
const COLUMN_HEIGHT = ROW_HEIGHT * VISIBLE_ROWS;
const PADDING = (COLUMN_HEIGHT - ROW_HEIGHT) / 2;

/**
 * A single scrollable, snap-to-center wheel column — day, month, or year.
 * Uses native CSS scroll-snap for momentum/snapping instead of hand-rolled
 * drag physics, so it inherits the browser's own scroll feel for free.
 *
 * `options` is an array of { value, label } — `value` is what gets reported
 * on change, `label` is what's shown (lets month show "Jan" while value is 1).
 */
const WheelColumn = ({ options, value, onChange, isUnset = false }) => {
  const containerRef = useRef(null);
  const scrollTimeout = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const didMountEmit = useRef(false);

  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));

  // If the parent has no stored value yet, the column still shows *something*
  // (the option nearest the passed default). Report that up on mount so "what's
  // centred is what's saved" — otherwise tapping Continue without scrolling
  // fails validation with a confusing "enter a value" error. Only fires when
  // the field is genuinely unset, so a restored/prefilled value is never lost.
  useEffect(() => {
    if (didMountEmit.current || !isUnset) return;
    didMountEmit.current = true;
    const centered = options[selectedIndex];
    if (centered) onChange(centered.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep scroll position in sync when `value` changes from outside
  // (e.g. day column re-clamping when month/year changes).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const targetTop = selectedIndex * ROW_HEIGHT;
    if (Math.abs(el.scrollTop - targetTop) > 1) {
      isProgrammaticScroll.current = true;
      el.scrollTo({ top: targetTop, behavior: 'auto' });
      // Clear the flag on the next frame — scrollTo without smooth behavior
      // resolves synchronously enough that a rAF is a safe guard.
      requestAnimationFrame(() => { isProgrammaticScroll.current = false; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, options.length]);

  const handleScroll = () => {
    if (isProgrammaticScroll.current) return;
    clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      const el = containerRef.current;
      if (!el) return;
      const index = Math.round(el.scrollTop / ROW_HEIGHT);
      const clamped = Math.min(Math.max(index, 0), options.length - 1);
      const option = options[clamped];
      if (option && option.value !== value) {
        onChange(option.value);
      }
    }, 120);
  };

  return (
    <div
      style={{
        position: 'relative',
        height: `${COLUMN_HEIGHT}px`,
        flex: 1,
      }}
    >
      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{
          height: '100%',
          overflowY: 'auto',
          scrollSnapType: 'y mandatory',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
          maskImage: 'linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          cursor: 'grab',
        }}
        className="cipher-wheel-column"
      >
        <div style={{ height: `${PADDING}px` }} />
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <div
              key={option.value}
              style={{
                height: `${ROW_HEIGHT}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                scrollSnapAlign: 'center',
                fontSize: isSelected ? '20px' : '17px',
                fontWeight: isSelected ? 700 : 400,
                color: isSelected ? theme.text : theme.textMuted,
                transition: 'color 0.15s, font-size 0.15s',
              }}
            >
              {option.label}
            </div>
          );
        })}
        <div style={{ height: `${PADDING}px` }} />
      </div>
      {/* Center selection band — purely visual, ignores pointer events */}
      <div
        style={{
          position: 'absolute',
          top: `${PADDING}px`,
          left: 0,
          right: 0,
          height: `${ROW_HEIGHT}px`,
          borderTop: `1px solid ${theme.surface3}`,
          borderBottom: `1px solid ${theme.surface3}`,
          pointerEvents: 'none',
        }}
      />
      <style>{`.cipher-wheel-column::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
};

export default WheelColumn;
export { ROW_HEIGHT, COLUMN_HEIGHT };
